import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { OrderButton } from "@/components/site/OrderContext";
import { NAV, LINKS } from "@/content/site";
import { EASE } from "@/components/site/motion";

const MobileMenu = ({ onClose }) => (
  <motion.div
    data-testid="mobile-menu"
    data-lenis-prevent
    className="fixed inset-0 z-[80] flex flex-col overflow-y-auto bg-[#ff2e63] px-6 pb-8 pt-28 text-[#fff5e8] lg:hidden"
    initial={{ clipPath: "circle(0% at 92% 6%)" }}
    animate={{ clipPath: "circle(150% at 92% 6%)" }}
    exit={{ clipPath: "circle(0% at 92% 6%)" }}
    transition={{ duration: 0.6, ease: EASE }}
  >
    <nav className="flex flex-col gap-1">
      {NAV.map((n, i) => (
        <motion.div key={n.to} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, ease: EASE, duration: 0.6 }}>
          <NavLink to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `block font-display text-[13vw] font-extrabold uppercase leading-[0.95] tracking-tight ${isActive ? "text-[#1a0a10]" : ""}`}>
            {n.label}
          </NavLink>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto pt-10">
      <OrderButton className="btn btn-ink w-full py-5 text-base" testId="mobile-order-online-btn">Order online</OrderButton>
      <div className="mt-6 flex items-center justify-between text-sm font-semibold">
        <NavLink to="/careers" onClick={onClose} data-testid="mobile-nav-careers" className="underline underline-offset-4">Careers</NavLink>
        <div className="flex gap-3">
          <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-instagram" className="grid h-11 w-11 place-items-center rounded-full bg-[#1a0a10]/15"><Instagram className="h-5 w-5" /></a>
          <a href={LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mobile-facebook" className="grid h-11 w-11 place-items-center rounded-full bg-[#1a0a10]/15"><Facebook className="h-5 w-5" /></a>
        </div>
      </div>
    </div>
  </motion.div>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header data-testid="site-nav" className="fixed inset-x-0 top-0 z-[90] px-3 pt-3 sm:px-5">
        <div className={`mx-auto flex max-w-[1440px] items-center justify-between gap-4 rounded-full py-2 pl-3 pr-2 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled && !open ? "bg-[#fff5e8]/80 shadow-[0_10px_40px_-18px_rgba(26,10,16,0.5)] backdrop-blur-xl" : "bg-transparent"}`}>
          <Logo />
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-wide transition-colors duration-300 ${isActive ? "bg-[#1a0a10] text-[#fff5e8]" : "text-[#1a0a10] hover:bg-[#1a0a10]/10"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <OrderButton className={`btn hidden px-5 py-3 sm:inline-flex ${scrolled ? "btn-berry" : "btn-ink"}`} testId="nav-order-online-btn">Order online</OrderButton>
            <button type="button" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-toggle" className="grid h-12 w-12 place-items-center rounded-full bg-[#1a0a10] text-[#fff5e8] lg:hidden">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
